import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FounderData {
  name: string;
  title: string;
  image: string;
  imageAlt: string;
  quote: string;
  background: string[];
  transformation: string;
}

interface FounderStoryProps {
  founder: FounderData;
}

const FounderStory: React.FC<FounderStoryProps> = ({ founder }) => {
  return (
    <section className="py-20 pb-12 bg-card">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            The Founder's Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            A personal transformation that sparked a global movement
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-lg">
              <AppImage
                src={founder.image}
                alt={founder.imageAlt}
                fill
                className="object-cover"
              />
            </div>
            <div className="bg-primary/5 p-8 rounded-xl border-l-4 border-primary">
              <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-primary mb-4" />
              <blockquote className="text-xl font-quote italic text-foreground leading-relaxed">
                "{founder.quote}"
              </blockquote>
              <div className="mt-4">
                <p className="font-headline font-bold text-foreground">{founder.name}</p>
                <p className="text-sm text-muted-foreground">{founder.title}</p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center">
                <Icon name="AcademicCapIcon" size={28} className="text-secondary mr-3" />
                Professional Background
              </h3>
              <div className="space-y-4">
                {founder.background.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success mt-1 flex-shrink-0" />
                    <p className="text-foreground font-body">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary/10 to-accent/10 p-8 rounded-xl">
              <h3 className="text-2xl font-headline font-bold text-foreground mb-4 flex items-center">
                <Icon name="SparklesIcon" size={28} className="text-accent mr-3" />
                The Transformation
              </h3>
              <p className="text-foreground font-body leading-relaxed whitespace-pre-line text-justify">
                {founder.transformation}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderStory;